import { FileDocumentType } from '@nkr/enums/document-type';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { AuthActionCreators } from '@nkr/store/authorization';
import { FileActionCreators } from '@nkr/store/file/actions';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { useEffect, useState } from 'react';

export interface IOpoFileResponse {
    documentTypeId: number,
    documentType: string,
    files: IOpoFile[]
}

export interface IOpoFileUploadResponse {
    success: boolean,
    results: string
}

export interface IOpoFile {
    id: number,
    opoId: number,
    fileId: number,
    rootLocation: string,
    path: string,
    uploadedOn: string,
    uploadedById: number
    uploadedByName: string
}

interface IRetVal {
    list: IOpoFileResponse[],
    loading: boolean,
    error: string,
    refresh: () => void,
    upload: (opoId: number, documentType: number, file: File) => Promise<ITypedBaseResponse<IOpoFileUploadResponse>>,
    download: (opoId: number, type: FileDocumentType) => Promise<any>
}

const useOpoFiles = (opoId: number): IRetVal => {
    const { dispatch } = useAppHookContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [list, setList] = useState<IOpoFileResponse[]>([]);

    const fetchData = async (opoId: number) => {
        let config = {
            url: `${__API__}/admin/opo/${opoId}/files`,
        };

        try {
            const response: ITypedBaseResponse<IOpoFileResponse[]> = await dispatch(AuthActionCreators.apiGet(config));

            if (response && response.data.results && response.data.success) {
                setList(response.data.results);
            } else {
                setError('Unable to load OPO files list');
                setList([]);
            }
        } catch (err) {
            setList([]);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const upload = async (opoId: number, documentType: number, file: File) => {
        let formData = new FormData();
        formData.append('file', file);

        let config = {
            url: `${__API__}/file/upload/${documentType}/${opoId}`,
            data: formData
        };

        try {
            const response: ITypedBaseResponse<IOpoFileUploadResponse> = await dispatch(AuthActionCreators.apiPostFormData(config));

            if (response && response.data.results && response.data.success) {
                //setList(response.data.results);
                return response;
            } else {
                setError('Unable to load OPO files list');
                //setList([]);
            }
        } catch (err) {
            // setList([]);
            setError(err);
        } finally {
            setLoading(false);
        }
    }

    const download = async (opoId: number, type: FileDocumentType) => {
        try {
            const response: any = await dispatch(FileActionCreators.downloadFile(type, opoId));

            if (response) {
                return response;
            } else {
                setError('Unable to load OPO files list');
                //setList([]);
            }
        } catch (err) {
            // setList([]);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        setError('');

        if (opoId > 0) {
            fetchData(opoId);
        }
    }, []);

    useEffect(() => {
        if (opoId > 0) {
            fetchData(opoId);
        }
    }, [opoId]);

    const refresh = () => {
        fetchData(opoId);
    };

    return { loading, error, list, refresh, upload, download };
}

export default useOpoFiles;
